import AssetUpload from '../../AssetUpload';
import ImageSpecifications from '../../ImageSpecifications';
import React, { useEffect, useState } from 'react';
import { Checkbox, Form, InputNumber, Modal } from 'antd';
import { IOffer } from '../../../interfaces/offers';
import { UploadFile } from 'antd/es/upload/interface';
import { useModalReturnType, useUpdate } from '@refinedev/core';

// Validation function for offer banners
const validateOfferBanner = async (file: UploadFile): Promise<boolean> => {
  // Accept any image file - size limit is handled by maxSize prop
  return file.type?.startsWith('image/') ?? false;
};

// Interface for offer media/assets
interface IOfferMedia {
  desktop_banner?: string;
  mobile_banner?: string;
  timer_minutes?: number;
}

// Extend IOffer to include media property
interface IOfferWithMedia extends IOffer {
  media?: IOfferMedia;
}

interface PersonalizeOfferModalProps {
  modal: useModalReturnType;
  offer: IOfferWithMedia;
  onUpdate: () => void;
}

export const PersonalizeOfferModal: React.FC<PersonalizeOfferModalProps> = ({
  modal,
  offer,
  onUpdate
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [timerEnabled, setTimerEnabled] = useState(
    !!offer.media?.timer_minutes
  );

  const { mutateAsync: updateOffer } = useUpdate();

  // Set initial form values when modal opens
  useEffect(() => {
    if (modal.visible) {
      const initialValues = {
        desktop_banner: offer.media?.desktop_banner,
        mobile_banner: offer.media?.mobile_banner,
        timer_enabled: !!offer.media?.timer_minutes,
        timer_minutes: offer.media?.timer_minutes || 30
      };

      form.setFieldsValue(initialValues);
      setTimerEnabled(!!offer.media?.timer_minutes);
    }
  }, [modal.visible, offer.media, form]);

  const handleSave = async () => {
    try {
      setLoading(true);
      await form.validateFields();

      const values = form.getFieldsValue();

      // Use timer directly as integer minutes
      const timer =
        timerEnabled && values.timer_minutes ? values.timer_minutes : null;

      await updateOffer({
        resource: 'offers',
        id: offer.id,
        values: {
          media: {
            desktop_banner: values.desktop_banner,
            mobile_banner: values.mobile_banner,
            timer_minutes: timer
          }
        },
        successNotification: () => ({
          type: 'success',
          message: 'Sucesso!',
          description: 'Oferta personalizada com sucesso'
        }),
        errorNotification: () => ({
          type: 'error',
          message: 'Erro!',
          description: 'Não foi possível personalizar a oferta'
        })
      });

      modal.close();
      onUpdate();
    } catch {
      // Error is already handled by errorNotification
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setTimerEnabled(false);
    modal.close();
  };

  return (
    <Modal
      title={`Personalizar Oferta - ${offer.name}`}
      visible={modal.visible}
      onOk={handleSave}
      onCancel={handleCancel}
      okText="Salvar"
      cancelText="Cancelar"
      confirmLoading={loading}
      destroyOnClose
      className="!w-[800px]"
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          desktop_banner: offer.media?.desktop_banner,
          mobile_banner: offer.media?.mobile_banner,
          timer_enabled: !!offer.media?.timer_minutes,
          timer_minutes: offer.media?.timer_minutes || 30
        }}
      >
        <ImageSpecifications
          title="Banner Desktop"
          size="500kb"
          format="jpeg/jpg/png"
        />
        <Form.Item name="desktop_banner">
          <AssetUpload
            src={offer.media?.desktop_banner}
            accept="image/jpeg,image/png"
            type="offer"
            maxSize={{
              size: 0.5,
              message: 'O arquivo deve ter no máximo 500KB'
            }}
            onValidate={validateOfferBanner}
          />
        </Form.Item>

        <ImageSpecifications
          title="Banner Mobile"
          size="500kb"
          format="jpeg/jpg/png"
        />
        <Form.Item name="mobile_banner">
          <AssetUpload
            src={offer.media?.mobile_banner}
            accept="image/jpeg,image/png"
            type="offer"
            maxSize={{
              size: 0.5,
              message: 'O arquivo deve ter no máximo 500KB'
            }}
            onValidate={validateOfferBanner}
          />
        </Form.Item>

        <div style={{ marginTop: 24 }}>
          <Form.Item
            name="timer_enabled"
            valuePropName="checked"
            style={{ marginBottom: 16 }}
          >
            <Checkbox onChange={e => setTimerEnabled(e.target.checked)}>
              Habilitar cronômetro na oferta
            </Checkbox>
          </Form.Item>

          <Form.Item
            label="Timer em minutos"
            name="timer_minutes"
            tooltip="Tempo em minutos para o contador"
            rules={[
              {
                required: timerEnabled,
                message: 'Por favor, insira o tempo em minutos'
              }
            ]}
          >
            <InputNumber
              disabled={!timerEnabled}
              placeholder="30"
              min={1}
              addonAfter="minutos"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};
